import React from 'react';
import { IonButton, IonIcon, IonModal } from '@ionic/react';
import { calendarOutline, locationOutline, peopleOutline, timeOutline } from 'ionicons/icons';
import { useTranslation } from 'react-i18next';
import { formatDate } from '@/utils/dateUtils';

export interface GameInvitation {
  id: string;
  senderId: string;
  senderName: string;
  senderHcp?: string;
  courseName: string;
  courseLocation?: string;
  gameDate: Date | string;
  gameTime: string;
  gameType: string;
  players: number;
  message?: string;
}

interface GameInvitationModalProps {
  isOpen: boolean;
  onClose: () => void;
  invitation: GameInvitation | null;
  onAccept: (invitationId: string) => void;
  onDecline: (invitationId: string) => void;
}

const GameInvitationModal: React.FC<GameInvitationModalProps> = ({
  isOpen,
  onClose,
  invitation,
  onAccept,
  onDecline
}) => {
  const { t } = useTranslation();

  if (!invitation) return null;

  return (
    <IonModal isOpen={isOpen} onDidDismiss={onClose} className="game-invitation-modal">
      <div className="p-4 flex flex-col h-full">
        {/* Header with avatar and name */}
        <div className="flex items-center mb-4">
          <div className="w-16 h-16 rounded-full bg-gray-200 flex items-center justify-center overflow-hidden border-2 border-primary">
            <img 
              src={`/assets/images/avatars/avatar-${typeof invitation.senderId === 'string' ? parseInt(invitation.senderId) % 10 : 1}.jpg`} 
              alt={invitation.senderName}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/assets/images/avatar-placeholder.jpg';
              }}
            />
          </div>
          <div className="ml-4">
            <h2 className="text-xl font-bold">{invitation.senderName}</h2>
            {invitation.senderHcp && (
              <p className="text-gray-600">HCP {invitation.senderHcp}</p>
            )}
            <p className="text-gray-500">{t('invited_you_to_play')}</p>
          </div>
        </div>

        {/* Course image and details */}
        <div className="bg-gray-100 rounded-xl overflow-hidden mb-4">
          <div className="h-40 bg-gray-300 relative">
            <img 
              src={`/assets/images/courses/${invitation.courseName.toLowerCase().replace(/\s+/g, '-')}.jpg`}
              alt={invitation.courseName}
              className="w-full h-full object-cover"
              onError={(e) => {
                (e.target as HTMLImageElement).src = '/assets/images/course-placeholder.jpg';
              }}
            />
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
              <h3 className="text-white text-xl font-bold">{invitation.courseName}</h3>
              {invitation.courseLocation && (
                <div className="flex items-center text-white/90">
                  <IonIcon icon={locationOutline} className="mr-1" />
                  <span>{invitation.courseLocation}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Game details */}
        <div className="grid grid-cols-2 gap-3 mb-4">
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center text-gray-500 mb-1">
              <IonIcon icon={calendarOutline} className="mr-2" />
              <span className="text-sm">{t('date')}</span>
            </div>
            <div className="font-medium">
              {typeof invitation.gameDate === 'string' 
                ? invitation.gameDate 
                : formatDate(invitation.gameDate)}
            </div>
          </div>
          
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center text-gray-500 mb-1">
              <IonIcon icon={timeOutline} className="mr-2" />
              <span className="text-sm">{t('time')}</span>
            </div>
            <div className="font-medium">{invitation.gameTime}</div>
          </div>
          
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center text-gray-500 mb-1">
              <IonIcon icon={peopleOutline} className="mr-2" />
              <span className="text-sm">{t('players')}</span>
            </div>
            <div className="font-medium">
              {invitation.players}/4 {t('players')}
            </div>
          </div>
          
          <div className="bg-gray-50 p-3 rounded-lg">
            <div className="flex items-center text-gray-500 mb-1">
              <IonIcon className="mr-2" />
              <span className="text-sm">{t('game_type')}</span>
            </div>
            <div className="font-medium">{invitation.gameType}</div>
          </div>
        </div>

        {/* Message */}
        {invitation.message && (
          <div className="bg-gray-50 p-4 rounded-lg mb-4">
            <div className="text-gray-500 mb-1 text-sm">{t('message')}</div>
            <div className="italic text-gray-700">"{invitation.message}"</div>
          </div>
        )}

        {/* Action buttons */}
        <div className="mt-auto grid grid-cols-2 gap-3">
          <IonButton 
            color="medium" 
            expand="block"
            onClick={() => onDecline(invitation.id)}
            className="m-0"
          >
            {t('decline')}
          </IonButton>
          <IonButton 
            color="success" 
            expand="block"
            onClick={() => onAccept(invitation.id)}
            className="m-0"
          >
            {t('accept')}
          </IonButton>
        </div>
      </div>
    </IonModal>
  );
};

export default GameInvitationModal;
